import React, { useEffect, useRef } from "react";
import { Box, Grid, Typography, Button, Avatar, Divider, Select, MenuItem } from "@mui/material";
import TrendsChart from "../../../components/charts/TrendsChart";
import EnemyFactions from "./EnemyFactions";
import ScenariosStats from "./ScenariosStats";
import PlayerStats from "./PlayerStats";
import { BCPArrowLeft } from "../../../components/icons/bcp/BCPIcons";

const LEADER_COLORS = (isDark) => [
  isDark ? "#8b5cf6" : "#7c3aed",
  isDark ? "#3b82f6" : "#2563eb",
  isDark ? "#4ade80" : "#16a34a",
  isDark ? "#fbbf24" : "#d97706",
  isDark ? "#f87171" : "#dc2626",
  isDark ? "#22d3ee" : "#0891b2"
];

const OVERALL_ROWS = [
  { key: "overall", label: "Overall", color: isDark => isDark ? "#9ca3af" : "#6b7280" },
  { key: "asAttacker", label: "Attacker", color: () => "#8b5cf6" },
  { key: "asDefender", label: "Defender", color: () => "#3b82f6" }
];

export default function FactionAnalyzer({ data, theme, onBack, factionRows, onFactionChange }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) {
      const top = containerRef.current.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }
  }, []);

  if (!data) return null;

  const isDark = theme.palette.mode === "dark";
  const { faction, overall, asAttacker, asDefender, leaders, monthlyTimeline } = data;

  const leaderColors = LEADER_COLORS(isDark);

  const containerSx = {
    backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
    borderRadius: 1,
    p: 2.5,
    boxShadow: isDark ? "0 2px 8px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.1)"
  };

  const getWinRateColor = (winRate) => {
    if (winRate >= 55) return isDark ? "#4ade80" : "#16a34a";
    if (winRate <= 45) return isDark ? "#f87171" : "#dc2626";
    return isDark ? "#9ca3af" : "#6b7280";
  };

  const getInitials = (name) => {
    if (!name) return "";
    const words = name.trim().split(" ");
    if (words.length === 1) return name.substring(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const statsMap = { overall, asAttacker, asDefender };

  const renderOverallRow = ({ key, label, color }, isLast) => {
    const stats = statsMap[key];
    const labelColor = color(isDark);
    const winRateColor = getWinRateColor(stats.winRate);

    return (
      <Box key={key}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.5 }}>
          <Box
            sx={{
              width: 68,
              flexShrink: 0,
              px: 0.75,
              py: 0.35,
              borderRadius: 0.75,
              border: `1px solid ${labelColor}44`,
              backgroundColor: `${labelColor}18`,
              textAlign: "center"
            }}
          >
            <Typography
              sx={{
                color: labelColor,
                fontSize: "0.6rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                lineHeight: 1.3
              }}
            >
              {label}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                mb: 0.75
              }}
            >
              <Typography sx={{ color: winRateColor, fontWeight: 700, fontSize: "0.875rem" }}>
                {stats.winRate}% win rate
              </Typography>
              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: "0.75rem",
                  fontVariantNumeric: "tabular-nums"
                }}
              >
                {stats.games.toLocaleString()} games
              </Typography>
            </Box>
            <Box
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
                overflow: "hidden"
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${stats.winRate}%`,
                  borderRadius: 3,
                  background: `linear-gradient(90deg, ${winRateColor}55, ${winRateColor})`
                }}
              />
            </Box>
          </Box>
        </Box>

        {!isLast && (
          <Divider sx={{ borderColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)" }} />
        )}
      </Box>
    );
  };

  return (
    <Box ref={containerRef}>
      <Divider sx={{ mb: 4, borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)" }} />

      <Typography
        variant="h5"
        sx={{
          textAlign: "center",
          mb: 2,
          color: theme.palette.text.primary,
          fontWeight: 600,
          letterSpacing: "0.05em"
        }}
      >
        Faction Analyzer
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          mb: 3,
          gap: 1.5
        }}
      >
        <Box>
          <Button
            size="small"
            variant="outlined"
            onClick={onBack}
            sx={{
              height: 34,
              px: 1.5,
              fontSize: "0.75rem",
              fontWeight: 600,
              textTransform: "none",
              borderColor: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
              color: theme.palette.text.secondary,
              "&:hover": {
                borderColor: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
                backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"
              }
            }}
          >
            <BCPArrowLeft /> Back
          </Button>
        </Box>

        <Box>
          {factionRows && factionRows.length > 0 && (
            <Select
              value={faction.id}
              size="small"
              onChange={(e) => {
                const selected = factionRows.find(r => r.faction.id === e.target.value);
                if (selected) onFactionChange(selected);
              }}
              renderValue={(value) => {
                const row = factionRows.find(r => r.faction.id === value);
                if (!row) return null;
                return (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Avatar
                      src={row.faction.icon || undefined}
                      sx={{ width: 20, height: 20, fontSize: "0.55rem", fontWeight: 700 }}
                    >
                      {!row.faction.icon && getInitials(row.faction.name)}
                    </Avatar>
                    <Typography
                      sx={{ fontSize: "0.875rem", fontWeight: 700, color: theme.palette.text.primary }}
                    >
                      {row.faction.name}
                    </Typography>
                  </Box>
                );
              }}
              MenuProps={{
                PaperProps: {
                  sx: { backgroundColor: theme.palette.background.paper }
                }
              }}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)"
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)"
                },
                "& .MuiSelect-select": {
                  py: 0.75,
                  px: 1.5,
                  backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"
                },
                "& .MuiSvgIcon-root": { color: theme.palette.text.secondary }
              }}
            >
              {factionRows.map((row) => (
                <MenuItem key={row.faction.id} value={row.faction.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar
                      src={row.faction.icon || undefined}
                      sx={{ width: 24, height: 24, fontSize: "0.6rem", fontWeight: 700 }}
                    >
                      {!row.faction.icon && getInitials(row.faction.name)}
                    </Avatar>
                    <Typography sx={{ fontSize: "0.875rem" }}>{row.faction.name}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          )}
        </Box>

        <Box />
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box sx={containerSx}>
              <Typography
                variant="h6"
                sx={{ mb: 2, color: theme.palette.text.secondary, textAlign: "center" }}
              >
                Overall Statistics
              </Typography>

              {OVERALL_ROWS.map((row, i) =>
                renderOverallRow(row, i === OVERALL_ROWS.length - 1)
              )}
            </Box>

            {leaders && leaders.length > 0 && (
              <Box sx={containerSx}>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: theme.palette.text.secondary, textAlign: "center" }}
                >
                  Leaders
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {leaders.map((leader, i) => {
                    const leaderColor = leaderColors[i % leaderColors.length];
                    const winRateColor = getWinRateColor(leader.winRate);
                    const isLast = i === leaders.length - 1;
                    return (
                      <Box key={i}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.25 }}>
                          <Avatar
                            src={leader.icon || undefined}
                            sx={{
                              width: 30,
                              height: 30,
                              fontSize: "0.65rem",
                              fontWeight: 700,
                              backgroundColor: !leader.icon ? leaderColor : undefined,
                              flexShrink: 0
                            }}
                          >
                            {!leader.icon && getInitials(leader.name)}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "baseline",
                                justifyContent: "space-between",
                                mb: 0.5
                              }}
                            >
                              <Typography sx={{ color: winRateColor, fontWeight: 700, fontSize: "0.8rem" }}>
                                {leader.winRate}% win rate
                              </Typography>
                              <Typography
                                sx={{
                                  color: theme.palette.text.secondary,
                                  fontSize: "0.7rem",
                                  fontVariantNumeric: "tabular-nums"
                                }}
                              >
                                {leader.games.toLocaleString()} games
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                height: 5,
                                borderRadius: 3,
                                backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
                                overflow: "hidden",
                                mb: 0.4
                              }}
                            >
                              <Box
                                sx={{
                                  height: "100%",
                                  width: `${leader.winRate}%`,
                                  borderRadius: 3,
                                  background: `linear-gradient(90deg, ${winRateColor}55, ${winRateColor})`
                                }}
                              />
                            </Box>
                            <Typography
                              sx={{
                                color: theme.palette.text.secondary,
                                fontSize: "0.7rem",
                                fontWeight: 500
                              }}
                            >
                              {leader.name}
                            </Typography>
                          </Box>
                        </Box>
                        {!isLast && (
                          <Divider
                            sx={{
                              borderColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"
                            }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={7}>
          <Box sx={{ ...containerSx, display: "flex", flexDirection: "column" }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, color: theme.palette.text.secondary }}
            >
              Performance Over Time
            </Typography>

            <TrendsChart
              data={monthlyTimeline}
              barDataKey="games"
              lineDataKey="winRate"
              barLabel="Games"
              lineLabel="Win Rate"
              barColor="#a855f7"
              lineColor="#22d3ee"
              barAxisLabel="Games"
              lineAxisLabel="Win Rate"
              height={482}
            />
          </Box>
        </Grid>
      </Grid>

      {data.enemyFactions && data.enemyFactions.length > 0 && (
        <EnemyFactions data={data.enemyFactions} theme={theme} />
      )}

      {data.scenarios && (
        <ScenariosStats data={data.scenarios} theme={theme} />
      )}

      <PlayerStats data={data} theme={theme} />
    </Box>
  );
}
